/**
 * Spec 160 (research D-160-06) — le serveur refuse l'accès au client affiché.
 *
 * Même forme que `sessionPerdue.ts`, pour la même raison : l'émetteur est `apiClient`, qui n'a pas accès à l'arbre
 * React ; un drapeau hors React lu par `useSyncExternalStore` est la forme prévue pour ce cas.
 *
 * Il n'est levé que sur `403 ACCOUNT_ACCESS_DENIED` — le refus de `assertTenantAccess`. Les autres 403 du serveur
 * (simulations fermées au client, scénario non visible, droits d'administration…) restent l'affaire de l'écran qui les
 * reçoit : les traiter comme « vous n'avez pas accès à ce client » dirait à un Client qu'il n'a pas accès à sa propre
 * organisation (contrainte G2 du garde-fou avant le plan).
 *
 * Il retient le CLIENT de la requête refusée, et la coquille ne l'applique qu'au client affiché (`refusVautPour`) : un
 * 403 tardif de la page précédente, ou une écriture refusée sur un autre client, ne remplace pas la page en cours par le
 * refus (revue à froid du 2026-10-11). La coquille l'oublie à chaque changement d'adresse.
 */

export interface RefusClient {
  /** Client désigné par la requête refusée ; `null` quand la requête ne le dit pas. */
  accountId: string | null
}

let refus: RefusClient | null = null
const abonnes = new Set<() => void>()

function notifier(): void {
  for (const abonne of abonnes) abonne()
}

export function signalerAccesClientRefuse(accountId: string | null = null): void {
  if (refus && refus.accountId === accountId) return
  refus = { accountId }
  notifier()
}

export function oublierAccesClientRefuse(): void {
  if (!refus) return
  refus = null
  notifier()
}

/** Instantané stable pour `useSyncExternalStore` : le même objet tant que rien ne change. */
export function accesClientRefuse(): RefusClient | null {
  return refus
}

export function accesClientEstRefuse(): boolean {
  return refus !== null
}

/** Le refus vaut-il pour ce client affiché ? Sans client identifiable dans la requête, oui. */
export function refusVautPour(r: RefusClient | null, accountIdAffiche: string | null): boolean {
  if (!r || !accountIdAffiche) return false
  return r.accountId === null || r.accountId.toLowerCase() === accountIdAffiche.toLowerCase()
}

const GUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const PAR_PARAMETRE = new RegExp(`[?&]accountId=(${GUID})`, 'i')
const PAR_CHEMIN = new RegExp(`/(?:accounts|clients)/(${GUID})(?:[/?#]|$)`, 'i')

/** Le client que désigne une requête de l'API, par son paramètre `accountId` ou par son chemin ; sinon `null`. */
export function compteDeLaRequete(chemin: string): string | null {
  return PAR_PARAMETRE.exec(chemin)?.[1] ?? PAR_CHEMIN.exec(chemin)?.[1] ?? null
}

/** Contrat `useSyncExternalStore` : rend la fonction de désabonnement. */
export function surAccesClientRefuse(abonne: () => void): () => void {
  abonnes.add(abonne)
  return () => {
    abonnes.delete(abonne)
  }
}
